import { Text } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { PageContainer } from '../components/shared/PageContainer'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared/ResponsiveTable'
import { EmailStatusChip } from '../components/communications/EmailStatusChip'
import { useEmailMessages } from '../hooks/useCommunications'
import { formatShortDate } from '../lib/formatDate'
import type { EmailMessageSummary } from '../types/communications'

const columns: ResponsiveColumn<EmailMessageSummary>[] = [
  {
    header: 'Subject',
    mobileRole: 'title',
    cell: (r) => <Text fontWeight="medium">{r.subject}</Text>,
  },
  {
    header: 'Source',
    mobileRole: 'meta',
    cell: (r) => <Text fontSize="sm" color="fg.muted">{r.sourceName}</Text>,
  },
  {
    header: 'Sent',
    mobileRole: 'desktop-only',
    isNumeric: true,
    cell: (r) => r.sentCount,
  },
  {
    header: 'Suppressed',
    mobileRole: 'desktop-only',
    isNumeric: true,
    cell: (r) => r.suppressedCount,
  },
  {
    header: 'Failed',
    mobileRole: 'desktop-only',
    isNumeric: true,
    cell: (r) => r.failedCount,
  },
  {
    header: 'Status',
    mobileRole: 'badge',
    cell: (r) => <EmailStatusChip status={r.status} />,
  },
  {
    header: 'Created by',
    mobileRole: 'desktop-only',
    cell: (r) => <Text fontSize="sm" color="fg.muted">{r.createdByUserId}</Text>,
  },
  {
    header: 'Date',
    mobileRole: 'meta',
    cell: (r) => <Text fontSize="sm" color="fg.muted">{formatShortDate(r.createdAt)}</Text>,
  },
]

/** Spec §8 `/communications/email` -- newest-first sent list, row click -> detail. `useEmailMessages`
 * is a capped `take` fetch (no server-side skip/offset for this endpoint, see useCommunications.ts),
 * so "paginated" here means the same single-capped-page shape as ListsPage/EmailSuppressionsPage,
 * not a page-through UI. */
export function EmailListPage() {
  const navigate = useNavigate()
  const { data, isLoading, error } = useEmailMessages()

  const rows = [...(data ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  )

  return (
    <PageContainer title="Sent email" subtitle="Emails sent to Lobby Day attendees and contact lists.">
      {!!error && (
        <Text color="danger.fg" mb={4}>Could not load sent email.</Text>
      )}
      <ResponsiveTable
        columns={columns}
        rows={rows}
        keyFn={(r) => r.id}
        onRowClick={(r) => navigate(`/dashboard/communications/email/${r.id}`)}
        isLoading={isLoading}
        emptyMessage="No emails sent yet."
      />
    </PageContainer>
  )
}
